import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';

/**
 * Square footage calculator drawer component.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} calculateButton
 * @property {HTMLButtonElement} updateQuantityButton
 * @property {HTMLInputElement} lengthFt
 * @property {HTMLInputElement} lengthIn
 * @property {HTMLInputElement} widthFt
 * @property {HTMLInputElement} widthIn
 * @property {HTMLInputElement} directSqft
 * @property {HTMLInputElement} addWaste
 * @property {HTMLElement} wasteDropdownWrapper
 * @property {HTMLButtonElement} wasteDropdownTrigger
 * @property {HTMLElement} wasteDropdownMenu
 * @property {HTMLElement} wasteDropdownValue
 * @property {HTMLElement} resultMainContainer
 * @property {HTMLElement} resultContainer
 * @property {HTMLElement} resultText
 *
 * @extends {Component<Refs>}
 */
class CalculateSqFtDrawer extends Component {
  /**
   * Calculated square footage value.
   * @type {number}
   */
  calculatedSqft = 0;

  /**
   * Calculated units needed.
   * @type {number}
   */
  calculatedUnits = 0;

  /**
   * Waste percentage (default 10%).
   * @type {number}
   */
  wastePercentage = 10;

  connectedCallback() {
    super.connectedCallback();
    
    requestIdleCallback(() => {
      this.#init();
    });
  }

  /**
   * Initialize the calculator after refs are ready
   */
  #init() {
    const {
      calculateButton,
      updateQuantityButton,
      lengthFt,
      lengthIn,
      widthFt,
      widthIn,
      directSqft,
      addWaste,
      wasteDropdownWrapper
    } = this.refs;

    if (!calculateButton) return;

    addWaste?.addEventListener('change', () => {
      if (addWaste.checked) {
        wasteDropdownWrapper.style.display = 'block';
      } else {
        wasteDropdownWrapper.style.display = 'none';
      }
    });

    this.#setupDropdown();

    directSqft?.addEventListener('input', () => {
      if (directSqft.value) {
        lengthFt.value = '';
        lengthIn.value = '';
        widthFt.value = '';
        widthIn.value = '';
      }
    });

    [lengthFt, lengthIn, widthFt, widthIn].forEach(input => {
      input?.addEventListener('input', () => {
        if (input.value) {
          directSqft.value = '';
        }
      });
    });

    calculateButton?.addEventListener('click', () => this.#handleCalculate());

    updateQuantityButton?.addEventListener('click', () => {
        this.#handleUpdateQuantity()

        const closeButton = this.parentElement?.parentElement.querySelector('.popup-link__close');
        if (closeButton) {
          setTimeout(() => {
            closeButton.click();
          }, 1000);
        }
      }
    );
  }

  /**
   * Setup dropdown functionality
   */
  #setupDropdown() {
    const { wasteDropdownTrigger, wasteDropdownMenu } = this.refs;
    
    if (!wasteDropdownTrigger || !wasteDropdownMenu) return;
    
    wasteDropdownTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#toggleDropdown();
    });
    
    const items = wasteDropdownMenu.querySelectorAll('.calculate-sq-ft-form__dropdown-item');
    items.forEach(item => {
      item.addEventListener('click', (e) => {
        const button = /** @type {HTMLButtonElement} */ (e.currentTarget);
        const value = button.dataset.value || '10';
        const text = button.textContent?.trim() || '10%';
        
        this.wastePercentage = parseFloat(value);
        this.refs.wasteDropdownValue.textContent = text;
        
        items.forEach(i => i.removeAttribute('aria-selected'));
        button.setAttribute('aria-selected', 'true');
        
        this.#closeDropdown();
      });
    });
    
    document.addEventListener('click', (e) => {
      const dropdown = this.querySelector('.calculate-sq-ft-form__dropdown');
      if (dropdown && !dropdown.contains(/** @type {Node} */ (e.target))) {
        this.#closeDropdown();
      }
    });
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#closeDropdown();
      }
    });
  }

  /**
   * Toggle dropdown open/close
   */
  #toggleDropdown() {
    const { wasteDropdownTrigger, wasteDropdownMenu } = this.refs;
    if (!wasteDropdownTrigger || !wasteDropdownMenu) return;
    
    const isExpanded = wasteDropdownTrigger.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      this.#closeDropdown();
    } else {
      this.#openDropdown();
    }
  }

  /**
   * Open dropdown
   */
  #openDropdown() {
    const { wasteDropdownTrigger, wasteDropdownMenu } = this.refs;
    if (!wasteDropdownTrigger || !wasteDropdownMenu) return;
    
    wasteDropdownTrigger.setAttribute('aria-expanded', 'true');
    wasteDropdownMenu.setAttribute('aria-hidden', 'false');
  }

  /**
   * Close dropdown
   */
  #closeDropdown() {
    const { wasteDropdownTrigger, wasteDropdownMenu } = this.refs;
    if (!wasteDropdownTrigger || !wasteDropdownMenu) return;
    
    wasteDropdownTrigger.setAttribute('aria-expanded', 'false');
    wasteDropdownMenu.setAttribute('aria-hidden', 'true');
  }

  /**
   * Handle calculate button click
   */
  #handleCalculate() {
    const { lengthFt, lengthIn, widthFt, widthIn, directSqft, addWaste, resultMainContainer, resultText } = this.refs;
    
    let sqft = 0;

    if (directSqft.value) {
      sqft = parseFloat(directSqft.value) || 0;
    } else {
      const lengthFeet = (parseFloat(lengthFt.value) || 0) + ((parseFloat(lengthIn.value) || 0) / 12);
      const widthFeet = (parseFloat(widthFt.value) || 0) + ((parseFloat(widthIn.value) || 0) / 12);
      sqft = lengthFeet * widthFeet;
    }

    if (addWaste.checked) {
      const percentage = this.wastePercentage / 100;
      sqft = sqft * (1 + percentage);
    }

    if (sqft > 0) {
      this.calculatedSqft = sqft;
      
      
      const isStarCross = document.querySelector('quantity-calculator-star-cross');
      
      if (isStarCross) {
        const firstSqFtInput = document.querySelector('#first_quantity_sq_ft');
        const secondSqFtInput = document.querySelector('#second_quantity_sq_ft');
        
        const firstCovers = parseFloat(firstSqFtInput?.dataset?.covers || '0');
        const secondCovers = parseFloat(secondSqFtInput?.dataset?.covers || '0');
        
        const halfSqft = sqft / 2;
        const firstUnits = firstCovers > 0 ? Math.ceil(halfSqft / firstCovers) : 0;
        const secondUnits = secondCovers > 0 ? Math.ceil(halfSqft / secondCovers) : 0;
        
        resultText.textContent = `We recommend ${firstUnits} boxes (stars) and ${secondUnits} boxes (crosses)`;
      } else {
        const sqFtInput = document.querySelector('#quantity_sq_ft');
        const unitsLabel = document.querySelector('[data-unit]');
        const covers = parseFloat(sqFtInput?.dataset?.covers || '0');
        const unitName = unitsLabel?.dataset?.unit || 'units';
        
        this.calculatedUnits = covers > 0 ? Math.ceil(sqft / covers) : 0;
        
        let displayUnit = unitName;
        if (this.calculatedUnits === 1) {
          if (unitName === 'boxes') {
            displayUnit = 'box';
          } else if (unitName === 'sheets') {
            displayUnit = 'sheet';
          }
        }
        
        resultText.textContent = 'We recommend ' + this.calculatedUnits + ' ' + displayUnit;
      }
      
      resultMainContainer.style.display = 'block';
    } else {
      resultMainContainer.style.display = 'none';
    }
  }

  /**
   * Handle update quantity button click
   */
  #handleUpdateQuantity() {
    const { addWaste } = this.refs;
    
    if (this.calculatedSqft <= 0) return;
    
    const isStarCross = document.querySelector('quantity-calculator-star-cross');
    
    if (isStarCross) {
      const firstSqFtInput = document.querySelector('#first_quantity_sq_ft');
      const secondSqFtInput = document.querySelector('#second_quantity_sq_ft');
      
      if (firstSqFtInput && secondSqFtInput) {
        if (addWaste.checked) {
          const mainDropdownItems = document.querySelectorAll('.quantity-calculator__dropdown-item[data-value]');
          const targetItem = Array.from(mainDropdownItems).find(item => item.dataset.value === String(this.wastePercentage));

          if (targetItem) {
            targetItem.click();
          }
        } else {
          const noOverageItem = document.querySelector('.quantity-calculator__dropdown-item[data-value="0"]');
          if (noOverageItem) {
            noOverageItem.click();
          }
        }
        
        const halfSqft = this.calculatedSqft / 2;
        
        firstSqFtInput.value = halfSqft.toFixed(2);
        firstSqFtInput.dispatchEvent(new Event('input', { bubbles: true }));
        
        secondSqFtInput.value = halfSqft.toFixed(2);
        secondSqFtInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } else {
      const sqFtInput = document.querySelector('#quantity_sq_ft');
      if (sqFtInput) {
        if (addWaste.checked) {
          const mainDropdownItems = document.querySelectorAll('.quantity-calculator__dropdown-item[data-value]');
          const targetItem = Array.from(mainDropdownItems).find(item => item.dataset.value === String(this.wastePercentage));

          if (targetItem) {
            targetItem.click();
          }
          
        } else {
          const noOverageItem = document.querySelector('.quantity-calculator__dropdown-item[data-value="0"]');
          if (noOverageItem) {
            noOverageItem.click();
          }
        }

        sqFtInput.value = this.calculatedSqft.toFixed(4);
        sqFtInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    
    const closeButton = document.querySelector('.popup-link__close');
    if (closeButton) {
      closeButton.click();
    }
  }
}

if (!customElements.get('calculate-sq-ft-drawer')) {
  customElements.define('calculate-sq-ft-drawer', CalculateSqFtDrawer);
}
